import { useTranslation } from 'react-i18next';
import { BanknotesIcon, CreditCardIcon } from '@heroicons/react/20/solid';
import type { OrderStatus, PaymentMethod, PaymentStatus } from '../../types/Order';
import { Chip, ChipProps } from './Chip';
import { COLLECT_PAYMENT, ORDER_STATUS, PAYMENT_STATUS } from './status';

/** Order status: icon, translated label, tone. Never interactive. Key it by status to play the swap animation. */
export const StatusChip = ({ status, size }: { status: OrderStatus; size?: ChipProps['size'] }) => {
    const { t } = useTranslation();
    const meta = ORDER_STATUS[status];
    return (
        <Chip key={status} variant={meta.tone} icon={meta.icon} size={size} className="animate-swap-in">
            {t(meta.labelKey)}
        </Chip>
    );
};

export interface PaymentChipProps {
    status: PaymentStatus;
    method?: PaymentMethod;
    /** Pass the order status to get "Collect payment" for unpaid orders that are ready or completed. */
    orderStatus?: OrderStatus;
    size?: ChipProps['size'];
}

export const PaymentChip = ({ status, method, orderStatus, size }: PaymentChipProps) => {
    const { t } = useTranslation();
    const due = status === 'unpaid' && (orderStatus === 'ready' || orderStatus === 'completed');
    const meta = due ? COLLECT_PAYMENT : PAYMENT_STATUS[status];
    const icon = status === 'paid' && method === 'card' ? CreditCardIcon : meta.icon ?? (due ? BanknotesIcon : undefined);
    const label = status === 'paid' && method ? `${t(meta.labelKey)} · ${t(`payment.${method}`)}` : t(meta.labelKey);
    return (
        <Chip key={`${status}${due}`} variant={meta.tone} icon={icon} size={size} className="animate-swap-in">
            {label}
        </Chip>
    );
};
